import { useEffect, useState } from "react";
import { ShippingData } from "../../types";

interface ShippingInfoColumn {
    name: string;
    visiable: boolean;
}

interface InfoTableProps {
    shippingInfoColumns: ShippingInfoColumn[];
    shippingData: ShippingData[];
    shippment: string;
}

const InfoTable = (props: InfoTableProps) => {
    const [totalQty, setTotalQty] = useState(0);
    const [totalWaitingQty, setTotalWaitingQty] = useState(0);
    const [totalShippedQty, setTotalShippedQty] = useState(0);

    function truncateText(text: string | null, maxLength: number = 20): string {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    }

    function isDateOlderThanToday(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return inputDate < today;
    }

    function isDateInTwoDays(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();
        const twoDaysFromNow = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        twoDaysFromNow.setHours(0, 0, 0, 0);
        twoDaysFromNow.setDate(today.getDate() + 2);

        return inputDate.getTime() === twoDaysFromNow.getTime();
    }

    function getNextStepColor(date: string | undefined) {
        if (!date) {
            return "#fff";
        }

        if (isDateOlderThanToday(date)) {
            return "#fa7f52ff";
        } else if (isDateInTwoDays(date)) {
            return "#e0f052ff";
        } else {
            return "#43bd38ff";
        }
    }

    function handleGoToNextStepAction(id: number) {
        window.open(
            `https://odoo-ps-psae-carandx.odoo.com/web#id=${id}&cids=3&menu_id=309&action=470&model=purchase.order&view_type=form`,
            "_blank",
            "noopener,noreferrer"
        );
    }

    useEffect(() => {
        let lTotalQty = 0;
        let lTotalWaitingQty = 0;
        let lTotalShippedQty = 0;

        for (let i = 0; i < props.shippingData.length; i++) {
            const data = props.shippingData[i];

            lTotalQty += data.qty;
            lTotalWaitingQty += data.waiting_qty;
            lTotalShippedQty += data.shipped_qty;
        }

        setTotalQty(lTotalQty);
        setTotalWaitingQty(lTotalWaitingQty);
        setTotalShippedQty(lTotalShippedQty);
    }, [props.shippingData]);

    return (
        <table
            className="table table-hover mb-0"
            style={{
                borderRight: "1px solid #efefef",
                position: "sticky",
                left: 0,
            }}
        >
            {/* Table Header */}
            <thead
                style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#0d6efd",
                    color: "white",
                    zIndex: 10,
                }}
            >
                <tr>
                    {props.shippingInfoColumns.map((col) => {
                        if (col?.visiable) {
                            return (
                                <th
                                    key={col.name}
                                    className="fw-semibold text-uppercase"
                                    style={{
                                        fontSize: "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                        color: "#6c757d",
                                        padding: "0.6rem",
                                        borderBottom: "2px solid #dee2e6",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {col.name}
                                </th>
                            );
                        }
                    })}
                </tr>
            </thead>
            {/* Table Body */}
            <tbody>
                {props.shippingData.map((row, idx) => (
                    <tr
                        key={idx}
                        style={{
                            borderBottom: "1px solid #f0f0f0",
                            fontSize: 10,
                            height: 50,
                        }}
                    >
                        {props.shippingInfoColumns.map((column) => (
                            <>
                                {props.shippment !== "customer" &&
                                    column.name == "PO NUMBER" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                fontWeight: "500",
                                                color: "#495057",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    overflow: "visible",
                                                }}
                                            >
                                                {truncateText(row.po_number)}
                                            </div>
                                        </td>
                                    )}

                                {props.shippment != "customer" &&
                                    column.name == "VENDOR REF" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                fontWeight: "500",
                                                color: "#495057",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {truncateText(
                                                    row.vendor_reference
                                                )}
                                            </div>
                                        </td>
                                    )}

                                {props.shippment !== "customer" &&
                                    column.name == "BL NUMBER" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                fontWeight: "500",
                                                color: "#495057",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    overflow: "visible",
                                                }}
                                            >
                                                {row.bl_number || ""}
                                            </div>
                                        </td>
                                    )}
                                {column.name == "SO Name" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                fontWeight: "500",
                                                color: "#495057",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    overflow: "visible",
                                                }}
                                            >
                                                {row.origin || "-"}
                                            </div>
                                        </td>
                                    )}

                                {column.name == "Product" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                fontSize:
                                                    "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                                fontWeight: "500",
                                                color: "#495057",
                                                minWidth: 30,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    overflow: "visible",
                                                }}
                                            >
                                                {row.products.length > 1 ? (
                                                    <select
                                                        style={{
                                                            padding: "2px 6px",
                                                            border: "1px solid #dee2e6",
                                                            borderRadius: "4px",
                                                            backgroundColor:
                                                                "white",
                                                            cursor: "pointer",
                                                            fontSize: "0.75rem",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <option disabled>
                                                            Products (
                                                            {
                                                                row.products
                                                                    .length
                                                            }
                                                            )
                                                        </option>
                                                        {row.products.map(
                                                            (
                                                                product: any,
                                                                index: number
                                                            ) => (
                                                                <option
                                                                    key={index}
                                                                >
                                                                    {truncateText(
                                                                        typeof product ===
                                                                            "string"
                                                                            ? product
                                                                            : product.name ||
                                                                                  product,
                                                                        50
                                                                    )}
                                                                    (
                                                                    {
                                                                        product.qty
                                                                    }
                                                                    )
                                                                </option>
                                                            )
                                                        )}
                                                    </select>
                                                ) : (
                                                    <>
                                                        {props.shippment ==
                                                        "customer" ? (
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "-webkit-box",
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient:
                                                                        "vertical",
                                                                    overflow:
                                                                        "hidden",
                                                                    textOverflow:
                                                                        "ellipsis",
                                                                }}
                                                            >
                                                                {
                                                                    row
                                                                        .products[0]
                                                                        ?.name
                                                                }
                                                            </div>
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    flexDirection:
                                                                        "row",
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                    overflow:
                                                                        "hidden",
                                                                }}
                                                            >
                                                                {" "}
                                                                {
                                                                    row
                                                                        .products[0]
                                                                        ?.name
                                                                }
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    )}

                                {props.shippment !== "customer" &&
                                    column.name == "DROPSHIP" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                color: "#495057",
                                                fontSize: 12,
                                            }}
                                        >
                                            <span
                                                className="badge badge-primary "
                                                style={{
                                                    backgroundColor:
                                                        row.is_dropship
                                                            ? "#039d0bff"
                                                            : "#818181ff",
                                                    margin: 0,
                                                }}
                                            >
                                                {row.is_dropship ? "yes" : "no"}
                                            </span>
                                        </td>
                                    )}

                                {column.name == "Qty Ordered" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                color: "#495057",
                                            }}
                                        >
                                            {row.qty || "-"}
                                        </td>
                                    )}

                                {props.shippment !== "customer" &&
                                    column.name == "Waiting QTY" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                color: "#495057",
                                            }}
                                        >
                                            {row.waiting_qty || "-"}
                                        </td>
                                    )}

                                {column.name == "Qty Shipped" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                color: "#495057",
                                            }}
                                        >
                                            {row.shipped_qty || "-"}
                                        </td>
                                    )}

                                {props.shippment !== "customer" &&
                                    column.name == "Next Step" &&
                                    column.visiable && (
                                        <td
                                            style={{
                                                color: "#202020ff",
                                                fontWeight: "700",
                                                backgroundColor:
                                                    getNextStepColor(
                                                        row.next_step
                                                            ?.date_deadline
                                                    ),
                                                whiteSpace: "nowrap",
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                handleGoToNextStepAction(row.id)
                                            }
                                        >
                                            {row.next_step?.name}
                                            <i className="bi bi-arrow-right fs-8 mx-1"></i>
                                        </td>
                                    )}
                            </>
                        ))}
                    </tr>
                ))}
            </tbody>
            <tfoot
                style={{
                    position: "sticky",
                    bottom: -1,
                    fontWeight: "600",
                    borderTop: "1px solid #ddd",
                    minHeight: 50,
                }}
            >
                <tr
                    style={{
                        borderBottom: "1px solid #f0f0f0",
                        fontSize: 12,
                    }}
                >
                    {props.shippingInfoColumns.map((column) => (
                        <>
                            {column.name == "SO Name" && column.visiable && (
                                <td
                                    style={{
                                        fontWeight: "500",
                                        color: "#495057",
                                        backgroundColor: "#efefef",
                                    }}
                                ></td>
                            )}

                            {column.name == "PO NUMBER" && column.visiable && (
                                <td
                                    style={{
                                        fontWeight: "500",
                                        color: "#495057",
                                        backgroundColor: "#efefef",
                                    }}
                                ></td>
                            )}

                            {column.name == "VENDOR REF" && column.visiable && (
                                <td
                                    style={{
                                        fontWeight: "500",
                                        color: "#495057",
                                        backgroundColor: "#efefef",
                                    }}
                                ></td>
                            )}

                            {column.name == "BL NUMBER" && column.visiable && (
                                <td
                                    style={{
                                        fontWeight: "500",
                                        color: "#495057",
                                        backgroundColor: "#efefef",
                                    }}
                                ></td>
                            )}

                            {column.name == "Product" && column.visiable && (
                                <td
                                    style={{
                                        fontWeight: "500",
                                        color: "#495057",
                                        backgroundColor: "#efefef",
                                    }}
                                ></td>
                            )}

                            {column.name == "DROPSHIP" && column.visiable && (
                                <td
                                    style={{
                                        color: "#495057",
                                        fontSize: 12,
                                        backgroundColor: "#efefef",
                                    }}
                                ></td>
                            )}

                            {column.name == "Qty Ordered" &&
                                column.visiable && (
                                    <td
                                        style={{
                                            color: "#495057",
                                            backgroundColor: "#efefef",
                                        }}
                                    >
                                        Total: {totalQty}
                                    </td>
                                )}

                            {column.name == "Waiting QTY" &&
                                column.visiable && (
                                    <td
                                        style={{
                                            color: "#495057",
                                            backgroundColor: "#efefef",
                                        }}
                                    >
                                        Total: {totalWaitingQty}
                                    </td>
                                )}

                            {column.name == "Qty Shipped" &&
                                column.visiable && (
                                    <td
                                        style={{
                                            color: "#495057",
                                            backgroundColor: "#efefef",
                                        }}
                                    >
                                        Total: {totalShippedQty}
                                    </td>
                                )}

                            {column.name == "Partner" && column.visiable && (
                                <td
                                    style={{
                                        color: "#495057",
                                        backgroundColor: "#efefef",
                                    }}
                                ></td>
                            )}

                            {column.name == "Next Step" && column.visiable && (
                                <td
                                    style={{
                                        color: "#495057",
                                        backgroundColor: "#efefef",
                                    }}
                                ></td>
                            )}
                        </>
                    ))}
                </tr>
            </tfoot>
        </table>
    );
};

export default InfoTable;
